import React, { useState, useEffect } from "react";
import { empleadosService } from "../services/api";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Table,
  Form,
  Button,
  Pagination,
  InputGroup,
  Row,
  Col,
} from "react-bootstrap";
import {
  FaSearch,
  FaEye,
  FaBell,
  FaBalanceScale,
  FaFileExcel,
  FaClock,
} from "react-icons/fa";
import { BsCheckCircleFill, BsDashCircleFill } from "react-icons/bs";
import { toast } from "react-toastify";
import AjustarHorasModal from "./AjustarHorasModal";

const PAGE_SIZE = 8;

const EmpleadosList = () => {
  const [empleados, setEmpleados] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedDepartamento, setSelectedDepartamento] = useState(-1);
  const [selectedActivo, setSelectedActivo] = useState(1);
  const [page, setPage] = useState(1);
  const [orderBy, setOrderBy] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showAjustarHorasModal, setShowAjustarHorasModal] = useState(false);
  const [selectedEmpleado, setSelectedEmpleado] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    pages: 1,
  });
  const navigate = useNavigate();

  // Función helper para verificar si FACTORIAL_ID es válido
  const isFactorialIdValid = (factorialId) => {
    if (factorialId === null || factorialId === undefined) return false;
    if (factorialId === 0 || factorialId === "0") return false;
    if (factorialId === "") return false;
    return true;
  };

  useEffect(() => {
    fetchDepartamentos();
  }, []);

  useEffect(() => {
    fetchEmpleados();
    // eslint-disable-next-line
  }, [page, orderBy, selectedDepartamento, selectedActivo]);

  const fetchDepartamentos = async () => {
    try {
      const res = await empleadosService.getDepartamentos();
      setDepartamentos(res.data.departamentos || []);
    } catch (err) {
      console.error("Error cargando departamentos:", err);
      setDepartamentos([]);
    }
  };

  const fetchEmpleados = async () => {
    setLoading(true);
    try {
      const res = await empleadosService.buscar({
        search,
        page,
        limit: PAGE_SIZE,
        orderBy,
        departamentoId: selectedDepartamento === -1 ? null : selectedDepartamento,
        activo: selectedActivo === -1 ? null : selectedActivo,
      });

      setEmpleados(res.data.empleados);
      if (res.data.pagination) {
        setPagination(res.data.pagination);
      } else {
        setPagination((prev) => ({
          ...prev,
          total: res.data.empleados.length,
          pages: 1,
        }));
      }
    } catch (err) {
      setEmpleados([]);
      setPagination({ page: 1, limit: PAGE_SIZE, total: 0, pages: 1 });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchEmpleados();
  };

  const handleOrder = (colIdx) => {
    setOrderBy(colIdx);
    setPage(1);
  };

  // Enviar recordatorio
  const handleEnviarRecordatorio = async (crewId) => {
    try {
      const resp = await empleadosService.enviarRecordatorio(crewId);
      if (resp.data.success) {
        toast.success("Mensaje enviado correctamente");
      } else {
        toast.error(resp.data.message || "Error al enviar recordatorio");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al enviar recordatorio"
      );
    }
  };

  // Cambiar estado activo/inactivo del empleado
  const handleToggleActive = async (crewId, newActive) => {
    try {
      const resp = await empleadosService.toggleActive(crewId, newActive);
      if (resp.data.success) {
        toast.success(newActive ? "Empleado activado" : "Empleado desactivado");
        fetchEmpleados(); // Recargar lista
      } else {
        toast.error(resp.data.message || "Error al cambiar estado");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al cambiar estado del empleado"
      );
    }
  };

  // Exportar balance de horas
  const handleExportarBalanceHoras = async () => {
    setExporting(true);
    try {
      const response = await empleadosService.exportarBalanceHoras(
        selectedYear
      );

      // Crear blob y descargar
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `balance_horas_${selectedYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Informe exportado correctamente");
    } catch (error) {
      console.error("Error exportando balance de horas:", error);
      toast.error(
        error.response?.data?.message || "Error al exportar el informe"
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Card className="mt-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-0">Empleados</h4>
            </Col>
            <Col xs="auto">
              <div className="d-flex align-items-center gap-2">
                <Form.Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{ width: "auto" }}
                  size="sm"
                >
                  {Array.from(
                    { length: 5 },
                    (_, i) => new Date().getFullYear() - 2 + i
                  ).map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </Form.Select>
                <Button
                  variant="success"
                  size="sm"
                  onClick={handleExportarBalanceHoras}
                  disabled={exporting}
                >
                  <FaFileExcel className="me-1" />
                  {exporting ? "Exportando..." : "Exportar Balance Horas"}
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <Form onSubmit={handleSearch} className="mb-3">
            <Row className="g-2">
              <Col xs={12} md={6}>
                <InputGroup>
                  <Form.Control
                    type="text"
                    placeholder="Buscar por nombre, email, teléfono o pasaporte..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <Button type="submit" variant="primary">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </Col>
              <Col xs={12} md={4}>
                <Form.Select
                  value={selectedDepartamento}
                  onChange={(e) => {
                    setSelectedDepartamento(parseInt(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={-1}>Todos los departamentos</option>
                  {departamentos.map((dep) => (
                    <option key={dep.ID} value={dep.ID}>
                      {dep.NOMBRE}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={2}>
                <Form.Select
                  value={selectedActivo}
                  onChange={(e) => {
                    setSelectedActivo(parseInt(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={-1}>Todos</option>
                  <option value={1}>Activo</option>
                  <option value={0}>Inactivo</option>
                </Form.Select>
              </Col>
            </Row>
          </Form>
          {/* Vista de tabla para desktop */}
          <div className="d-none d-md-block">
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th
                    onClick={() => handleOrder(0)}
                    style={{ cursor: "pointer" }}
                  >
                    CREW_ID
                  </th>
                  <th
                    onClick={() => handleOrder(3)}
                    style={{ cursor: "pointer" }}
                  >
                    NOMBRE
                  </th>
                  <th
                    onClick={() => handleOrder(5)}
                    style={{ cursor: "pointer" }}
                  >
                    EMAIL
                  </th>
                  <th
                    onClick={() => handleOrder(7)}
                    style={{ cursor: "pointer" }}
                  >
                    TELEFONO
                  </th>
                  <th
                    onClick={() => handleOrder(9)}
                    style={{ cursor: "pointer" }}
                  >
                    PAIS
                  </th>
                  <th
                    onClick={() => handleOrder(11)}
                    style={{ cursor: "pointer" }}
                  >
                    PASAPORTE
                  </th>
                  <th>ACTIVO</th>
                  <th>ESTADO</th>
                  <th>ÚLTIMA VALIDACIÓN</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {empleados.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center">
                      No hay empleados
                    </td>
                  </tr>
                )}
                {empleados.map((emp) => (
                  <tr key={emp.CREW_ID}>
                    <td>{emp.CREW_ID}</td>
                    <td>{emp.EMPLEADO}</td>
                    <td>{emp.EMAIL}</td>
                    <td>{emp.PHONE}</td>
                    <td>{emp.COUNTRY}</td>
                    <td>{emp.PASSPORT_NUMBER}</td>
                    <td className="text-center">
                      <Button
                        variant={emp.ACTIVE ? "outline-success" : "outline-danger"}
                        size="sm"
                        onClick={() => handleToggleActive(emp.CREW_ID, !emp.ACTIVE)}
                        title={emp.ACTIVE ? "Desactivar empleado" : "Activar empleado"}
                      >
                        {emp.ACTIVE ? <BsCheckCircleFill /> : <BsDashCircleFill />}
                      </Button>
                    </td>
                    <td>{emp.STATUS ? "Validado" : "No validado"}</td>
                    <td>
                      {emp.LAST_VALIDATED
                        ? new Date(emp.LAST_VALIDATED).toLocaleDateString(
                            "es-ES"
                          )
                        : ""}
                    </td>
                    <td>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={() =>
                          navigate(`/gastos?empleado_id=${emp.CREW_ID}`)
                        }
                        title="Ver gastos"
                      >
                        <FaEye />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="ms-2"
                        onClick={() =>
                          navigate(`/jornadas?empleado_id=${emp.CREW_ID}`)
                        }
                        title="Ver jornadas"
                      >
                        <FaClock />
                      </Button>
                      <Button
                        variant="outline-warning"
                        size="sm"
                        className="ms-2"
                        onClick={() => handleEnviarRecordatorio(emp.CREW_ID)}
                        title="Enviar recordatorio"
                      >
                        <FaBell />
                      </Button>
                      {isFactorialIdValid(emp.FACTORIAL_ID) && (
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="ms-2"
                          onClick={() => {
                            setSelectedEmpleado(emp.CREW_ID);
                            setShowAjustarHorasModal(true);
                          }}
                          title="Ajustar horas trabajadas/planificadas"
                        >
                          <FaBalanceScale />
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          {/* Vista de cards para móvil */}
          <div className="d-md-none">
            {empleados.length === 0 ? (
              <div className="text-center py-4">No hay empleados</div>
            ) : (
              <div className="gastos-cards">
                {empleados.map((emp) => (
                  <Card
                    key={emp.CREW_ID}
                    className={`mb-3 gasto-card ${
                      emp.STATUS ? "" : "table-danger"
                    }`}
                  >
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                          <h6 className="mb-1">
                            {emp.EMPLEADO}
                          </h6>
                          <small className="text-muted">
                            ID: {emp.CREW_ID}
                          </small>
                        </div>
                        <div className="d-flex align-items-center">
                          <span
                            className={`badge ${
                              emp.STATUS ? "bg-success" : "bg-warning text-dark"
                            }`}
                          >
                            {emp.STATUS ? "Validado" : "No validado"}
                          </span>
                        </div>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-12">
                          <small className="text-muted d-block">Email:</small>
                          <span>{emp.EMAIL}</span>
                        </div>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <small className="text-muted d-block">
                            Teléfono:
                          </small>
                          <span>{emp.PHONE}</span>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">País:</small>
                          <span>{emp.COUNTRY}</span>
                        </div>
                      </div>
                      <div className="row g-2 mb-2">
                        <div className="col-6">
                          <small className="text-muted d-block">
                            Pasaporte:
                          </small>
                          <span>{emp.PASSPORT_NUMBER}</span>
                        </div>
                        <div className="col-6">
                          <small className="text-muted d-block">
                            Última validación:
                          </small>
                          <span>
                            {emp.LAST_VALIDATED
                              ? new Date(emp.LAST_VALIDATED).toLocaleDateString(
                                  "es-ES"
                                )
                              : ""}
                          </span>
                        </div>
                      </div>
                      <div className="d-flex flex-wrap gap-1 mt-2">
                        <Button
                          variant={emp.ACTIVE ? "outline-success" : "outline-danger"}
                          size="sm"
                          onClick={() => handleToggleActive(emp.CREW_ID, !emp.ACTIVE)}
                          title={emp.ACTIVE ? "Desactivar empleado" : "Activar empleado"}
                        >
                          {emp.ACTIVE ? <BsCheckCircleFill /> : <BsDashCircleFill />}
                        </Button>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() =>
                            navigate(`/gastos?empleado_id=${emp.CREW_ID}`)
                          }
                          title="Ver gastos"
                        >
                          <FaEye />
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() =>
                            navigate(`/jornadas?empleado_id=${emp.CREW_ID}`)
                          }
                          title="Ver jornadas"
                        >
                          <FaClock />
                        </Button>
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={() => handleEnviarRecordatorio(emp.CREW_ID)}
                          title="Enviar recordatorio"
                        >
                          <FaBell />
                        </Button>
                        {isFactorialIdValid(emp.FACTORIAL_ID) && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => {
                              setSelectedEmpleado(emp.CREW_ID);
                              setShowAjustarHorasModal(true);
                            }}
                            title="Ajustar horas trabajadas/planificadas"
                          >
                            <FaBalanceScale />
                          </Button>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </div>
          {/* Paginación mejorada */}
          {pagination.pages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.First
                  onClick={() => setPage(1)}
                  disabled={pagination.page === 1}
                />
                <Pagination.Prev
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page === 1}
                />
                {/* Páginas dinámicas centradas en la actual */}
                {Array.from({ length: pagination.pages }, (_, i) => i + 1)
                  .filter(
                    (pageNum) =>
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 2 &&
                        pageNum <= pagination.page + 2)
                  )
                  .map((pageNum, idx, arr) => {
                    // Añadir puntos suspensivos si hay salto
                    if (idx > 0 && pageNum - arr[idx - 1] > 1) {
                      return [
                        <Pagination.Ellipsis
                          key={`ellipsis-${pageNum}`}
                          disabled
                        />,
                        <Pagination.Item
                          key={pageNum}
                          active={pageNum === pagination.page}
                          onClick={() => setPage(pageNum)}
                        >
                          {pageNum}
                        </Pagination.Item>,
                      ];
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                <Pagination.Next
                  onClick={() =>
                    setPage((p) => Math.min(pagination.pages, p + 1))
                  }
                  disabled={pagination.page === pagination.pages}
                />
                <Pagination.Last
                  onClick={() => setPage(pagination.pages)}
                  disabled={pagination.page === pagination.pages}
                />
              </Pagination>
            </div>
          )}
          {/* Leyenda informativa */}
          <div className="mt-3">
            <Card className="mb-0">
              <Card.Body>
                <h6 className="mb-2">Información de empleados</h6>
                <ul className="mb-2">
                  <li>
                    Todos los empleados se importan directamente desde{" "}
                    <strong>Rentman</strong>. Para <strong>añadir</strong> o{" "}
                    <strong>editar</strong> cualquier dato (nombre, email,
                    teléfono, pasaporte, etc.), debe realizarse en{" "}
                    <strong>Rentman</strong>.
                  </li>
                  <li>
                    Los teléfonos de <strong>Factorial</strong> tienen prioridad
                    sobre los de Rentman.
                  </li>
                  <li>
                    Los empleados marcados como <strong>inactivos</strong> no
                    serán tenidos en cuenta por el bot.
                  </li>
                </ul>
                <p className="mb-1">
                  <strong>Leyenda de acciones</strong>:
                </p>
                <div className="d-flex flex-column gap-1">
                  <div>
                    <span className="badge bg-primary me-2">
                      <FaSearch /> Buscar
                    </span>
                    Filtra por nombre, email, teléfono o pasaporte.
                  </div>
                  <div>
                    <span className="badge bg-secondary me-2">Ordenar</span>
                    Pulsa sobre el encabezado de columna para ordenar.
                  </div>
                  <div>
                    <span className="badge bg-info text-dark me-2">
                      <FaEye /> Ver gastos
                    </span>
                    Abre la vista de gastos filtrada por el empleado.
                  </div>
                  <div>
                    <span className="badge bg-secondary me-2">
                      <FaClock /> Ver jornadas
                    </span>
                    Abre la vista de jornadas filtrada por el empleado.
                  </div>
                  <div>
                    <span className="badge bg-warning text-dark me-2">
                      <FaBell /> Enviar recordatorio
                    </span>
                    Envía un recordatorio para que el empleado valide sus
                    gastos.
                  </div>
                  <div>
                    <span className="badge bg-primary me-2">
                      <FaBalanceScale /> Ajustar horas
                    </span>
                    Abre el modal para ajustar horas trabajadas/planificadas.
                  </div>
                  <div>
                    <span className="badge bg-success me-2">
                      <FaFileExcel /> Exportar balance horas
                    </span>
                    Genera el informe del año seleccionado. Usa el selector de
                    año para cambiarlo.
                  </div>
                  <div>
                    <span className="badge bg-success me-2">
                      <BsCheckCircleFill /> Activo
                    </span>
                    Empleado activo. Clic para desactivar.
                  </div>
                  <div>
                    <span className="badge bg-danger me-2">
                      <BsDashCircleFill /> Inactivo
                    </span>
                    Empleado inactivo. Clic para activar.
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </Card.Body>
      </Card>

      {/* Modal para ajustar horas */}
      <AjustarHorasModal
        show={showAjustarHorasModal}
        onHide={() => {
          setShowAjustarHorasModal(false);
          setSelectedEmpleado(null);
        }}
        empleadoId={selectedEmpleado}
      />
    </>
  );
};

export default EmpleadosList;
